import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, Clock, CheckCircle, XCircle, RefreshCw, Database, Archive } from 'lucide-react';
import { beesClient } from '../../api/beesClient.js';

interface ExportData {
  id: string;
  name: string;
  type: 'csv' | 'json' | 'xml' | 'pdf';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  size?: string;
  createdAt: string;
  downloadUrl?: string;
  progress?: number;
}

interface DataExportData {
  availableExports: Array<{
    id: string;
    name: string;
    description: string;
    type: 'csv' | 'json' | 'xml' | 'pdf';
    estimatedSize: string;
    lastExported?: string;
  }>;
  recentExports: ExportData[];
  stats: {
    totalExports: number;
    successfulExports: number;
    failedExports: number;
    totalDataSize: string;
  };
  activeExports: ExportData[];
}

const DataExportPanel: React.FC = () => {
  const [data, setData] = useState<DataExportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportingIds, setExportingIds] = useState<Set<string>>(new Set());

  const fetchDataExportData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await beesClient.runDataExport({ action: 'getExports' });
      
      if (response.success && response.data) {
        setData(response.data as DataExportData);
      } else {
        throw new Error(response.error || 'Failed to fetch data export information');
      }
    } catch (err) {
      console.error('DataExport API error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      
      setData({
        availableExports: [
          {
            id: 'repo-data',
            name: 'Repository Data',
            description: 'Complete repository statistics, commits, and metadata',
            type: 'json',
            estimatedSize: '2.5 MB',
            lastExported: new Date(Date.now() - 86400000).toISOString()
          },
          {
            id: 'user-activity',
            name: 'User Activity Report',
            description: 'User interactions, sessions, and activity patterns',
            type: 'csv',
            estimatedSize: '1.2 MB',
            lastExported: new Date(Date.now() - 172800000).toISOString()
          },
          {
            id: 'system-metrics',
            name: 'System Metrics',
            description: 'Performance metrics, health checks, and system logs',
            type: 'json',
            estimatedSize: '5.8 MB',
            lastExported: new Date(Date.now() - 3600000).toISOString()
          },
          {
            id: 'security-audit',
            name: 'Security Audit Log',
            description: 'Security events, access logs, and audit trail',
            type: 'pdf',
            estimatedSize: '3.1 MB'
          }
        ],
        recentExports: [
          {
            id: 'export-1',
            name: 'Repository Data',
            type: 'json',
            status: 'completed',
            size: '2.3 MB',
            createdAt: new Date(Date.now() - 3600000).toISOString(),
            downloadUrl: '/api/exports/repo-data-20240703.json'
          },
          {
            id: 'export-2',
            name: 'User Activity Report',
            type: 'csv',
            status: 'completed',
            size: '1.1 MB',
            createdAt: new Date(Date.now() - 7200000).toISOString(),
            downloadUrl: '/api/exports/user-activity-20240703.csv'
          },
          {
            id: 'export-3',
            name: 'System Metrics',
            type: 'json',
            status: 'failed',
            createdAt: new Date(Date.now() - 10800000).toISOString()
          }
        ],
        activeExports: [
          {
            id: 'export-4',
            name: 'Security Audit Log',
            type: 'pdf',
            status: 'processing',
            progress: 65,
            createdAt: new Date().toISOString()
          }
        ],
        stats: {
          totalExports: 24,
          successfulExports: 21,
          failedExports: 3,
          totalDataSize: '156.7 MB'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportId: string) => {
    if (exportingIds.has(exportId)) return;

    setExportingIds(prev => new Set(prev).add(exportId));
    
    try {
      const response = await beesClient.runDataExport({ 
        action: 'startExport', 
        exportId 
      });
      
      if (response.success) {
        await fetchDataExportData();
      } else {
        console.error('Failed to start export:', response.error);
        setError(`Failed to start export: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error starting export:', err);
      setError('Failed to start export');
    } finally {
      setExportingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(exportId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    fetchDataExportData();
    const interval = setInterval(fetchDataExportData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'processing': return <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-400" />;
      default: return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'csv': return <FileText className="w-4 h-4 text-green-400" />;
      case 'json': return <Database className="w-4 h-4 text-blue-400" />;
      case 'xml': return <FileText className="w-4 h-4 text-orange-400" />;
      case 'pdf': return <Archive className="w-4 h-4 text-red-400" />;
      default: return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-3">
              <RefreshCw className="w-6 h-6 text-brand-accent animate-spin" />
              <span className="text-brand-text text-lg">Loading export data...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
              <Download className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold cream-gradient-text text-drop-shadow mb-2">Data Export Panel</h1>
          <p className="text-brand-text-secondary text-lg">Export and download system data</p>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-900/20 border border-red-500/30 rounded-lg p-4"
          >
            <div className="flex items-center space-x-3">
              <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-red-400 font-medium">Data Export API Error</p>
                <p className="text-red-300 text-sm mt-1">{error}</p>
                <p className="text-red-300 text-xs mt-2">Showing mock data for demonstration</p>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: 'Total Exports', value: data?.stats.totalExports || 0, icon: Archive, color: 'text-blue-400' },
            { label: 'Successful', value: data?.stats.successfulExports || 0, icon: CheckCircle, color: 'text-green-400' },
            { label: 'Failed', value: data?.stats.failedExports || 0, icon: XCircle, color: 'text-red-400' },
            { label: 'Total Size', value: data?.stats.totalDataSize || '0 MB', icon: Database, color: 'text-purple-400' }
          ].map(({ label, value, icon: Icon, color }) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass-dark rounded-xl p-6 border border-white/10"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-text-secondary text-sm font-medium">{label}</p>
                  <p className="text-2xl font-bold text-brand-text mt-1">{value}</p>
                </div>
                <Icon className={`w-8 h-8 ${color}`} />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="glass-dark rounded-xl p-6 border border-white/10"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold cream-gradient-text text-drop-shadow">Available Exports</h2>
              <button
                onClick={fetchDataExportData}
                className="p-2 bg-brand-accent/20 hover:bg-brand-accent/30 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4 text-brand-accent" />
              </button>
            </div>
            
            <div className="space-y-4">
              {data?.availableExports?.map((exportItem) => (
                <div key={exportItem.id} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        {getTypeIcon(exportItem.type)}
                        <h3 className="font-medium cream-gradient-text text-drop-shadow">{exportItem.name}</h3>
                        <span className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded uppercase">
                          {exportItem.type}
                        </span>
                      </div>
                      <p className="text-brand-text-secondary text-sm">{exportItem.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-brand-text-secondary mt-2">
                        <span>Est. size: {exportItem.estimatedSize}</span>
                        {exportItem.lastExported && (
                          <span>Last: {new Date(exportItem.lastExported).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleExport(exportItem.id)}
                      disabled={exportingIds.has(exportItem.id)}
                      className="p-2 bg-blue-600/20 hover:bg-blue-600/30 disabled:bg-gray-600/20 rounded-lg transition-colors"
                    >
                      {exportingIds.has(exportItem.id) ? (
                        <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 text-blue-400" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-6"
          >
            {data?.activeExports && data.activeExports.length > 0 && (
              <div className="glass-dark rounded-xl p-6 border border-white/10">
                <h2 className="text-xl font-semibold cream-gradient-text text-drop-shadow mb-6">Active Exports</h2>
                <div className="space-y-4">
                  {data.activeExports.map((exportItem) => (
                    <div key={exportItem.id} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(exportItem.status)}
                          <h3 className="font-medium cream-gradient-text text-drop-shadow">{exportItem.name}</h3>
                        </div>
                        <span className="text-xs text-brand-text-secondary">
                          {exportItem.progress}%
                        </span>
                      </div>
                      {exportItem.progress && (
                        <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${exportItem.progress}%` }}
                          />
                        </div>
                      )}
                      <p className="text-xs text-brand-text-secondary">
                        Started: {new Date(exportItem.createdAt).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="glass-dark rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-semibold cream-gradient-text text-drop-shadow mb-6">Recent Exports</h2>
              <div className="space-y-4">
                {data?.recentExports?.map((exportItem) => (
                  <div key={exportItem.id} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(exportItem.status)}
                        <h3 className="font-medium cream-gradient-text text-drop-shadow">{exportItem.name}</h3>
                        {getTypeIcon(exportItem.type)}
                      </div>
                      {exportItem.downloadUrl && exportItem.status === 'completed' && (
                        <a
                          href={exportItem.downloadUrl}
                          className="p-1 bg-green-600/20 hover:bg-green-600/30 rounded text-green-400 transition-colors"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs text-brand-text-secondary">
                      <span>{new Date(exportItem.createdAt).toLocaleString()}</span>
                      {exportItem.size && <span>{exportItem.size}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DataExportPanel;
